import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { Target, Heart, Award, Users, ArrowRight, Linkedin, BadgeCheck } from 'lucide-react';
import { SEO } from '../components/SEO';
interface PageProps {
  openGetStarted?: () => void;
}
const experts = [{
  name: 'Paul Thurston',
  role: 'Lead Resume Strategist & Executive Career Coach',
  initials: 'PT',
  image: 'https://cdn.magicpatterns.com/uploads/dxrYbCc8JGi4Ats7JtUsah/unnamed.png',
  email: 'paulthurston@hireliocareerservices.com',
  linkedin: 'https://www.linkedin.com/in/paul-thurston-7b6614a1/',
  featured: true,
  credentials: ['Certified Professional Resume Writer (CPRW)', '18+ years in executive recruiting'],
  bio: 'A former Fortune 500 hiring director, Paul has personally reviewed over 50,000 resumes. He leads our executive practice and sets the standard every Hirelio writer is trained to meet, closing the gap between exceptional talent and the systems that screen them.',
  specialties: ['Executive Resumes', 'Career Strategy', 'ATS Optimization']
}, {
  name: 'Nathan Hurwitz',
  role: 'Senior Resume Writer',
  initials: 'NH',
  image: 'https://cdn.magicpatterns.com/uploads/syYZ9Xzo1VoyZDWsR2BSTH/1595357451007.jpg',
  linkedin: 'https://www.linkedin.com/in/nathan-hurwitz/?skipRedirect=true',
  featured: false,
  credentials: ['CPRW Certified', 'Ex-Tech Recruiter'],
  bio: 'Nathan specializes in technology and engineering roles, translating complex technical work into clear, recruiter-ready achievements.',
  specialties: ['Tech & Engineering', 'LinkedIn']
}, {
  name: 'Aaron Swarts',
  role: 'Resume Writer & Career Coach',
  initials: 'AS',
  image: 'https://cdn.magicpatterns.com/uploads/pZx73Mh757JsLrVWSsL9z9/MY_NEWEST_EXPERT.jpg',
  linkedin: 'https://linkedin.com/in/aaronswarts',
  featured: false,
  credentials: ['Certified Career Coach', '12+ years coaching'],
  bio: 'Aaron writes results-driven resumes and prepares clients to walk into any interview with confidence, with a proven record of helping professionals land roles and negotiate stronger offers.',
  specialties: ['Resume Writing', 'Interview Prep', 'Negotiation']
}, {
  name: 'Laura Smith',
  role: 'Career Branding Specialist',
  initials: 'LS',
  image: 'https://cdn.magicpatterns.com/uploads/aacu2VUtzfUMGDjSK1YnDc/WhatsApp_Image_2026-06-28_at_12.39.45_PM.jpg',
  featured: false,
  credentials: ['Certified Personal Branding Strategist', '10+ years'],
  bio: 'Laura crafts cohesive personal brands that make professionals memorable, aligning resumes, LinkedIn, and messaging into a single compelling story.',
  specialties: ['Personal Branding', 'LinkedIn', 'Storytelling']
}];
export function About({
  openGetStarted
}: PageProps) {
  const values = [{
    icon: <Target size={32} data-id="element-157" data-mp="r6mdb27tr406" />,
    title: 'Results-Driven',
    desc: 'We measure our success by your success. Our strategies are designed to get you more interviews and better offers.'
  }, {
    icon: <Heart size={32} data-id="element-158" data-mp="ew4vog9q5pzj" />,
    title: 'Human-Focused',
    desc: 'Behind every resume is a person with a unique story. We take the time to understand your individual career journey.'
  }, {
    icon: <Award size={32} data-id="element-159" data-mp="4yz1v09q7kvm" />,
    title: 'Excellence',
    desc: 'We maintain the highest standards in the industry, employing only certified, experienced writers.'
  }, {
    icon: <Users size={32} data-id="element-160" data-mp="xne74v322otr" />,
    title: 'Partnership',
    desc: 'We view our relationship with clients as a true partnership, working collaboratively to achieve your goals.'
  }];
  return <div className="w-full pt-20 pb-24" data-id="element-161" data-mp="omq2gqzjy0tj">
      <SEO title="About Hirelio | Expert Resume Writers & Career Services" description="Hirelio is a team of certified professional resume writers and former recruiters dedicated to helping job seekers stand out and get hired faster." data-id="element-162" data-mp="olog3qd35rwi" />
      {/* Hero */}
      <div className="bg-navy-900 text-white py-24 relative overflow-hidden" data-id="element-163" data-mp="113penc9sld5l">
        <div className="absolute inset-0 bg-grid opacity-20" data-id="element-164" data-mp="15ufmcw5oxs3g"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-165" data-mp="15wsbpuds34qj">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6" data-id="element-166" data-mp="r7mdo9qofkk8">
            Empowering Careers Worldwide
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto" data-id="element-167" data-mp="1jtysjjexytd4">
            Hirelio was founded with a simple mission: to help talented
            professionals overcome the hurdles of modern hiring and land the
            jobs they deserve.
          </p>
        </div>
      </div>

      {/* Story */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-168" data-mp="1eum3qt4ifc7w">
        <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-169" data-mp="3vk16rcuqpddt">
          <div data-id="element-170" data-mp="3viq0959wtrj0">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-6" data-id="element-171" data-mp="3vmdt027j69wo">
              Our Story
            </h2>
            <div className="space-y-4 text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-172" data-mp="3s12bxko3ddf8">
              <p data-id="element-173" data-mp="3n3sfvm6763if">
                The job search process is broken. Brilliant candidates are
                routinely rejected by automated systems before a human ever sees
                their resume. We saw this happening time and time again, and
                decided to do something about it.
              </p>
              <p data-id="element-174" data-mp="39cdq8whsb42e">
                Hirelio brings together former recruiters, HR professionals, and
                certified resume writers who understand exactly what it takes to
                stand out in today's competitive market. We know how Applicant
                Tracking Systems work, and more importantly, we know what hiring
                managers are looking for.
              </p>
              <p data-id="element-175" data-mp="2qjtcynekj7jp">
                Since our founding, we've helped over 10,000 professionals
                across 50+ industries secure better roles, negotiate higher
                salaries, and take control of their career trajectories.
              </p>
            </div>
          </div>
          <div className="relative" data-id="element-176" data-mp="3fafumb6o4wf8">
            <div className="aspect-square rounded-3xl overflow-hidden bg-slate-200 dark:bg-navy-800 relative" data-id="element-177" data-mp="3f9e7h8cdojh5">
              {/* Placeholder for team/office image */}
              <div className="absolute inset-0 bg-navy-900/30" data-id="element-178" data-mp="3p124rpvm8uqj"></div>
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Team collaborating" className="w-full h-full object-cover" data-id="element-179" data-mp="2sc2vc7japmwr" />
            </div>
            <div className="absolute -bottom-8 -left-8 bg-white dark:bg-navy-800 p-6 rounded-2xl shadow-xl border border-slate-100 dark:border-navy-700" data-id="element-180" data-mp="36assfn647ml4">
              <div className="text-4xl font-heading font-bold text-brand mb-1" data-id="element-181" data-mp="gl8lj39wh55s">
                10k+
              </div>
              <div className="text-sm font-medium text-slate-600 dark:text-slate-400" data-id="element-182" data-mp="lmxy4ncrtqvz">
                Careers Advanced
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Experts */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8" data-id="element-183" data-mp="1wd2q38fu93jj">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-184" data-mp="1wb9hhnlo098w">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 text-sm font-medium mb-4" data-id="element-185" data-mp="1rcybjnyhqivy">
            <Users size={14} data-id="element-186" data-mp="1ics1yd5wy322" /> Meet the Team
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4" data-id="element-187" data-mp="1ife9icst0ika">
            The Experts Behind Your Success
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400" data-id="element-188" data-mp="18gf91hi6zt8d">
            Certified writers and former recruiters who know exactly what it
            takes to get you hired.
          </p>
        </div>

        {/* Featured expert: Paul Thurston */}
        {experts.filter(e => e.featured).map(expert => <div key={expert.name} className="bg-white dark:bg-navy-800 rounded-3xl border border-slate-200 dark:border-navy-700 shadow-card overflow-hidden mb-8 flex flex-col md:flex-row" data-id="element-189" data-mp="2bq559gzdkvik">
              <div className="md:w-72 shrink-0 bg-navy-900 p-10 flex flex-col items-center justify-center text-center relative overflow-hidden" data-id="element-190" data-mp="26wki6jzblly5">
                <div className="absolute inset-0 bg-grid opacity-20" data-id="element-191" data-mp="1svzwng20tc0y"></div>
                {expert.image ? <img src={expert.image} alt={`${expert.name}, ${expert.role}`} className="relative z-10 w-28 h-28 rounded-full object-cover mb-5 shadow-lg shadow-brand/30 ring-2 ring-brand/40" data-id="element-192" data-mp="1xub5pokhrii4" /> : <div className="relative z-10 w-28 h-28 rounded-full bg-brand flex items-center justify-center text-white font-heading font-bold text-4xl mb-5 shadow-lg shadow-brand/30" data-id="element-193" data-mp="3e8jf0hg93vbt">
                    {expert.initials}
                  </div>}
                <h3 className="relative z-10 text-xl font-heading font-bold text-white" data-id="element-194" data-mp="12ollum0by4g9">
                  {expert.name}
                </h3>
                <p className="relative z-10 text-brand-100 text-sm font-medium mb-4" data-id="element-195" data-mp="1ggjsmq6kjo99">
                  {expert.role}
                </p>
                {expert.linkedin && <a href={expert.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${expert.name} on LinkedIn`} className="relative z-10 w-9 h-9 rounded-full bg-navy-800 hover:bg-brand text-slate-300 hover:text-white flex items-center justify-center transition-colors" data-id="element-196" data-mp="1qb1xvzrmvrv6">
                    <Linkedin size={18} data-id="element-197" data-mp="51mn85q2umfq" />
                  </a>}
                {expert.email && <p className="relative z-10 mt-3 text-xs text-slate-300 break-all" data-id="element-198" data-mp="3cdzm3yvjo6da">
                    {expert.email}
                  </p>}
              </div>
              <div className="p-8 md:p-10 flex-1" data-id="element-199" data-mp="37h0ubmzcsbi9">
                <div className="flex flex-wrap gap-2 mb-5" data-id="element-200" data-mp="33cjchtck08a4">
                  {expert.credentials.map(c => <span key={c} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400" data-id="element-201" data-mp="2yf9mocl95ulr">
                      <BadgeCheck size={14} data-id="element-202" data-mp="3mbxaqnhbg56f" /> {c}
                    </span>)}
                </div>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-6" data-id="element-203" data-mp="3hdvpbvwf3trs">
                  {expert.bio}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-204" data-mp="ioeu5bhq6fhx">
                  {expert.specialties.map(s => <span key={s} className="text-sm font-medium px-3 py-1 rounded-lg bg-slate-100 dark:bg-navy-700 text-slate-700 dark:text-slate-200" data-id="element-205" data-mp="duam0owq41f9">
                      {s}
                    </span>)}
                </div>
              </div>
            </div>)}

        {/* Supporting experts */}
        <div className="grid md:grid-cols-2 gap-8" data-id="element-206" data-mp="z56qwfrcl4m1">
          {experts.filter(e => !e.featured).map(expert => <div key={expert.name} className="bg-white dark:bg-navy-800 rounded-2xl border border-slate-200 dark:border-navy-700 shadow-sm p-8" data-id="element-207" data-mp="194xpotmxy5rx">
                <div className="flex items-center gap-4 mb-5" data-id="element-208" data-mp="1ivs81mx2gl83">
                  {expert.image ? <img src={expert.image} alt={`${expert.name}, ${expert.role}`} className="w-16 h-16 rounded-full object-cover shrink-0 ring-2 ring-brand/20" data-id="element-209" data-mp="1mxdsbtbxk1sb" /> : <div className="w-16 h-16 rounded-full bg-brand-50 dark:bg-navy-900 flex items-center justify-center text-brand font-heading font-bold text-xl shrink-0" data-id="element-210" data-mp="3jxi1n7iznv97">
                      {expert.initials}
                    </div>}
                  <div className="flex-1" data-id="element-211" data-mp="3swmzebmq7kkb">
                    <h3 className="text-lg font-heading font-bold text-navy-900 dark:text-white" data-id="element-212" data-mp="bhojko90ebq1">
                      {expert.name}
                    </h3>
                    <p className="text-sm text-brand font-medium" data-id="element-213" data-mp="6i3io8aa3tao">
                      {expert.role}
                    </p>
                  </div>
                  {expert.linkedin && <a href={expert.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${expert.name} on LinkedIn`} className="w-9 h-9 rounded-full bg-slate-100 dark:bg-navy-700 hover:bg-brand text-slate-500 hover:text-white flex items-center justify-center transition-colors shrink-0" data-id="element-214" data-mp="fkl4wmpxolxo">
                      <Linkedin size={18} data-id="element-215" data-mp="11tjmuw8v3qx1" />
                    </a>}
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-5" data-id="element-216" data-mp="s1w038n92y1w">
                  {expert.bio}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-217" data-mp="e136qr8oq90d">
                  {expert.specialties.map(s => <span key={s} className="text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-navy-700 text-slate-600 dark:text-slate-300" data-id="element-218" data-mp="j20ncze1quzr">
                      {s}
                    </span>)}
                </div>
              </div>)}
        </div>
      </div>

      {/* Values */}
      <div className="bg-slate-50 dark:bg-navy-900/50 py-24" data-id="element-219" data-mp="23frfmii2h8tn">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-220" data-mp="1eq4wb4lz36lv">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-221" data-mp="1eoc0kkcwe6sn">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4" data-id="element-222" data-mp="19sd3m8di7ggm">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400" data-id="element-223" data-mp="zt5slru8rxwo">
              The principles that guide everything we do.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-224" data-mp="vt3prfcrsyft">
            {values.map((value, idx) => <div key={idx} className="bg-white dark:bg-navy-800 p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-navy-700 text-center" data-id="element-225" data-mp="qyr551zk5h2h" data-mpi={idx}>
                <div className="text-brand mb-6 flex justify-center" data-id="element-226" data-mp="h0jqipsqrb3n">
                  {value.icon}
                </div>
                <h3 className="text-xl font-heading font-bold text-navy-900 dark:text-white mb-3" data-id="element-227" data-mp="81yab1aqs861">
                  {value.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed" data-id="element-228" data-mp="2jojs135lk0jl">
                  {value.desc}
                </p>
              </div>)}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-id="element-229" data-mp="2so6snmqi52bl">
        <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-6" data-id="element-230" data-mp="2xob95cretx1t">
          Ready to write your next chapter?
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-400 mb-10" data-id="element-231" data-mp="24znmbcr0izm5">
          Let our experts help you craft a narrative that opens doors.
        </p>
        <Link to="/contact" className="bg-brand hover:bg-brand-600 text-white px-8 py-4 rounded-xl font-medium text-lg transition-all shadow-lg shadow-brand/25 inline-flex items-center justify-center gap-2 mx-auto" data-id="element-232" data-mp="29xp0bj9l08qr">
          Work With Us <ArrowRight size={20} data-id="element-233" data-mp="1m8mlej6c0tzy" />
        </Link>
      </div>
    </div>;
}